/**
 * Membership — the role, and where the person is working.
 *
 * D53: the token carries which events you belong to; this document carries
 * what you are there. Security rules read the same document, so what the UI
 * shows and what the rules enforce cannot disagree.
 *
 * Checkpoints themselves live in `checkpoints.ts` — this file used to carry a
 * partial reader for the location chooser, and two readers of one collection
 * is how they drift.
 *
 * Rules permit the subject to write `checkpointId` on their own member
 * document and nothing else — `setLocation` below is that write. Attempting to
 * change `role` from here is refused by the emulator and by production alike,
 * and step 04 has failing tests proving it.
 */

import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';

import type { Role } from '@cairn/domain';

import { getServices } from './firebase.js';

/**
 * D58: the role list lives in `packages/domain` now, alongside the permission
 * matrix it maps to. `roaming` became `safety`, and `media` is new.
 */
export type { Role } from '@cairn/domain';

/**
 * Which shell a role lands on.
 *
 * Three, not two, since D58. `media` is phone-based and read-only — it is
 * neither the dense HQ board nor the touch-first checkpoint shell, and
 * squeezing it into either would show press a screen full of controls they
 * cannot use.
 */
export type Shell = 'checkpoint' | 'hq' | 'media';

export interface Membership {
  readonly eventId: string;
  readonly role: Role;
  /** null until the person says where they are. D43: never defaulted. */
  readonly checkpointId: string | null;
  readonly displayName: string | null;
}

const CHECKPOINT_ROLES: readonly Role[] = ['cpLead', 'cpCrew'];

/**
 * HQ roles land on the dense board; media get their own read-only view;
 * everyone else lands on the touch-first checkpoint shell. `safety` and
 * `medical` work from a phone in the field, so they belong on the checkpoint
 * side despite having no fixed checkpoint.
 */
export function shellFor(role: Role): Shell {
  if (role === 'media') return 'media';
  if (role === 'organiser' || role === 'hqAdmin' || role === 'hqViewer') return 'hq';
  return 'checkpoint';
}

/** True when this role must name a checkpoint before it can do anything. */
export function needsCheckpoint(role: Role): boolean {
  return CHECKPOINT_ROLES.includes(role);
}

export async function getMembership(eventId: string, uid: string): Promise<Membership | null> {
  const { db } = await getServices();
  const snapshot = await getDoc(doc(db, 'events', eventId, 'members', uid));
  if (!snapshot.exists()) return null;

  const data = snapshot.data();
  return {
    eventId,
    role: data['role'] as Role,
    checkpointId: (data['checkpointId'] as string | null | undefined) ?? null,
    displayName: (data['displayName'] as string | null | undefined) ?? null,
  };
}

/**
 * D34: sign-in records where the staff member actually is.
 * D40: relocation is sign-out then sign-in, so `null` on sign-out is normal.
 */
export async function setLocation(
  eventId: string,
  uid: string,
  checkpointId: string | null,
): Promise<void> {
  const { db } = await getServices();
  await updateDoc(doc(db, 'events', eventId, 'members', uid), { checkpointId });
}

/** Live membership, so an organiser changing a role is reflected immediately. */
export async function watchMembership(
  eventId: string,
  uid: string,
  onChange: (membership: Membership | null) => void,
): Promise<() => void> {
  const { db } = await getServices();

  return onSnapshot(doc(db, 'events', eventId, 'members', uid), (snapshot) => {
    if (!snapshot.exists()) {
      onChange(null);
      return;
    }
    const data = snapshot.data();
    onChange({
      eventId,
      role: data['role'] as Role,
      checkpointId: (data['checkpointId'] as string | null | undefined) ?? null,
      displayName: (data['displayName'] as string | null | undefined) ?? null,
    });
  });
}
